import { OrgDashboardQuery, useOrgDashboardQuery } from '@alice-financial/alice-sdk'
import { List, ListItem, ListItemIcon, ListItemText, Typography } from '@alice-financial/pretext-ui'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined'
import * as React from 'react'
import {
  CompanyRepresentativeComponent,
  CompanyRepresentativeInstructions,
} from '../../aliceCardProgram/KYC/CompanyRepresentative'
import { useOrgId } from '../../useOrgId'

export const KycStepTitle = 'Company Representative'

export const KycDescription = () => (
  <Typography variant="body2" color="text.secondary">
    Complete KYC verification for your company
  </Typography>
)

// Custom hook that polls the org dashboard query when there are pending verification requirements
const useOrgWithKycPolling = () => {
  const orgId = useOrgId()

  // Determine if we should poll based on pending verification requirements
  const determineRefetchInterval = React.useCallback((data: OrgDashboardQuery | undefined) => {
    const requirementsPendingVerification =
      data?.organization?.aliceCardProgram?.companyAccount?.requirementsPendingVerification
    const hasRequirementsPendingVerification =
      requirementsPendingVerification && requirementsPendingVerification.length > 0
    // Poll every 5 seconds if there are pending verification requirements
    return hasRequirementsPendingVerification ? 5000 : false
  }, [])

  const { data } = useOrgDashboardQuery(
    { orgId },
    {
      refetchInterval: determineRefetchInterval,
    }
  )

  return data?.organization
}

// Wrapper component that uses the core KYC components
export const KycComponent = () => {
  const org = useOrgWithKycPolling()

  if (!org) return null

  return (
    <>
      <CompanyRepresentativeInstructions />
      <CompanyRepresentativeComponent org={org} />
    </>
  )
}

export const KycInstructions = () => (
  <>
    <Typography variant="subtitle1" fontWeight="bold">
      Requirements
    </Typography>

    <List dense>
      <ListItem disableGutters>
        <ListItemIcon>
          <CheckCircleOutlineIcon fontSize="small" color="primary" />
        </ListItemIcon>
        <ListItemText primary="Business information - e.g. structure, EIN, website, address, etc." />
      </ListItem>
      <ListItem disableGutters>
        <ListItemIcon>
          <CheckCircleOutlineIcon fontSize="small" color="primary" />
        </ListItemIcon>
        <ListItemText
          primary={
            <span>
              Personal information - SSN and address for identity verification of the person providing
              compliance info.
            </span>
          }
        />
      </ListItem>
    </List>

    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
      Key Info
    </Typography>

    <List dense>
      <ListItem disableGutters>
        <ListItemIcon>
          <LightbulbOutlinedIcon fontSize="small" color="warning" />
        </ListItemIcon>
        <ListItemText primary="Alice uses Stripe for financial services and compliance checks. If you have used Stripe with OpenTable or Tripleseat, you will be able to complete this step even faster." />
      </ListItem>
      <ListItem disableGutters>
        <ListItemIcon>
          <LightbulbOutlinedIcon fontSize="small" color="warning" />
        </ListItemIcon>
        <ListItemText primary="The person submitting the compliance information for the business must be a legal US resident with a valid SSN." />
      </ListItem>
      <ListItem disableGutters>
        <ListItemIcon>
          <LightbulbOutlinedIcon fontSize="small" color="warning" />
        </ListItemIcon>
        <ListItemText
          primary={
            <span>
              This step is <strong>not</strong> connected to your credit and you are <strong>not</strong>{' '}
              making any financial committment to Stripe or Alice as an individual.
            </span>
          }
        />
      </ListItem>
    </List>
  </>
)
